import { useQuery } from '@apollo/client';
import * as inflection from 'inflection';
import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import TemplatesSuggestions from './TemplatesSuggestions';
import * as styles from './TemplatesView.module.scss';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import EmptyState from '../components/EmptyState';
import PageContent from '../components/PageContent';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { AllSlimPostsFragment } from '../graphql/generated/graphql';
import { PostType, TopicPostsOrder } from '../graphql/generated/graphql';
import topicsIcon from '../icons/non-prefilled/16x16/allTopics.svg';
import memberIcon from '../icons/non-prefilled/16x16/member.svg';
import orderIcon from '../icons/non-prefilled/16x16/order.svg';
import topicIcon from '../icons/non-prefilled/16x16/topic.svg';
import unorganizedIcon from '../icons/non-prefilled/16x16/unorganized.svg';
import PostFilterMenu from '../post/PostFilterMenu';
import { useAllSlimPostsWithoutTopics } from '../post/useAllSlimPosts';
import { TopicPostSectionSimpleList } from '../topic/posts/TopicPostSection';
import TopicBreadcrumbs from '../topic/TopicBreadcrumbs';
import TopicNameWithIcon from '../topic/TopicNameWithIcon';
import { useAllTopicMap } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';

const TEMPLATES_VIEW_PERSONAL_QUERY = gql(/** @complexity 4 */ `
  query TemplatesViewPersonal {
    session: currentSession {
      user {
        id
        membershipTopics(includeGroups: true) {
          id
        }
      }
    }
  }
`);

enum TemplateCategories {
  MINE = 'MINE',
  MY_TOPICS = 'MY_TOPICS',
  ALL_TOPICS = 'ALL_TOPICS',
  UNORGANIZED = 'UNORGANIZED',
}

const TEMPLATE_CATEGORIES_LABELS = {
  [TemplateCategories.MINE]: 'Mine',
  [TemplateCategories.MY_TOPICS]: 'My Topics',
  [TemplateCategories.ALL_TOPICS]: 'All Topics',
  [TemplateCategories.UNORGANIZED]: 'Unorganized',
} as const;

const TOPICS_METADATA_ATTRIBUTE = {
  [TopicPostsOrder.Popularity]: 'popularity',
  [TopicPostsOrder.PublishedAt]: 'publishedAt',
  [TopicPostsOrder.Title]: 'publishedAt',
  [TopicPostsOrder.UpdatedAt]: 'updatedAt',
  [TopicPostsOrder.ViewedAt]: 'userViewedAt',
} as const;

const SORT_TEMPLATES_TEMPLATE_FRAGMENT = gql(`
  fragment sortTemplates_template on SlimPostFields {
    id
    title
    views
    publishedAt
    updatedAt
    userViewedAt
  }
`);

const sortTemplates = <
  T extends DocumentType<typeof SORT_TEMPLATES_TEMPLATE_FRAGMENT>,
>(
  templates: T[],
  orderBy: TopicPostsOrder,
) =>
  templates.toSorted((a, b) => {
    switch (orderBy) {
      case TopicPostsOrder.Popularity:
        return b.views - a.views;
      case TopicPostsOrder.PublishedAt:
        return (
          new Date(b.publishedAt ?? 0).getTime() -
          new Date(a.publishedAt ?? 0).getTime()
        );
      case TopicPostsOrder.Title:
        return a.title.localeCompare(b.title);
      case TopicPostsOrder.UpdatedAt:
        return (
          new Date(b.updatedAt ?? 0).getTime() -
          new Date(a.updatedAt ?? 0).getTime()
        );
      case TopicPostsOrder.ViewedAt:
        return (
          new Date(b.userViewedAt ?? 0).getTime() -
          new Date(a.userViewedAt ?? 0).getTime()
        );
      default:
        return 0;
    }
  });

const sortTopics = (
  topicIds: string[],
  topicMap: ReturnType<typeof useAllTopicMap>,
) =>
  _.compact(
    topicIds.map((topicId) => {
      const topic = topicMap[topicId];
      // To each topic assign a path that consists of the topic name and all its ancestors
      return topic
        ? {
            topicId,
            path: [
              topic.name,
              ...topic.ancestors.map((ancestor) => ancestor.name),
            ].toReversed(), // From the highest ancestor to the topic itself
          }
        : null;
    }),
  )
    .toSorted(
      (a, b) =>
        a.path.length !== b.path.length
          ? a.path.length - b.path.length // Prefer higher level (fewer ancestors) topics
          : a.path.join('').localeCompare(b.path.join('')), // Same level sort alphabetically
    )
    .map((item) => item.topicId);

interface TemplatesViewProps {
  initialCategory?: TemplateCategories;
}

const TemplatesView = (props: TemplatesViewProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const topicMap = useAllTopicMap();
  const dispatch = useAppDispatch();
  const [category, setCategory] = useState<TemplateCategories>(
    props.initialCategory ?? TemplateCategories.MY_TOPICS,
  );
  const sortBy = useAppSelector((state) => state.ui.templatesOrderBy);

  const {
    data: personalData,
    loading,
    refetch,
  } = useQuery(TEMPLATES_VIEW_PERSONAL_QUERY);

  const allSlimPosts = useAllSlimPostsWithoutTopics();

  const allTemplates = useMemo(
    () =>
      allSlimPosts.filter(
        (post) =>
          post.publishedAt &&
          !post.archivedAt &&
          post.type === PostType.Template,
      ),
    [allSlimPosts],
  );

  const { mine, unorganized, myTopics, allTopics, current, topicIds } =
    useMemo(() => {
      const myTemplates = allTemplates.filter(
        (template) =>
          template.owner?.id === currentUser.id ||
          template.contributors.some(
            (contributor) => contributor.id === currentUser.id,
          ),
      );
      const myTemplateIdSet = new Set(
        myTemplates.map((template) => template.id),
      );

      const myTopicsIdSet = new Set(
        personalData?.session.user.membershipTopics.map((topic) => topic.id),
      );

      const mineTemplates = myTemplates.filter(
        (template) => template.topics.length === 0,
      );
      const allTopicsTemplates = allTemplates.filter(
        (template) => template.topics.length > 0,
      );
      const myTopicsTemplates = allTopicsTemplates.filter((template) =>
        template.topics.some((topic) => myTopicsIdSet.has(topic.id)),
      );
      const unorganizedTemplates = allTemplates.filter(
        (template) =>
          template.topics.length === 0 && !myTemplateIdSet.has(template.id),
      );

      let currentTemplates: typeof allTopicsTemplates = [];

      if (category === TemplateCategories.MINE) {
        currentTemplates = mineTemplates;
      } else if (category === TemplateCategories.MY_TOPICS) {
        currentTemplates = myTopicsTemplates;
      } else if (category === TemplateCategories.ALL_TOPICS) {
        currentTemplates = allTopicsTemplates;
      } else if (category === TemplateCategories.UNORGANIZED) {
        currentTemplates = unorganizedTemplates;
      }

      const topicIdsWithTemplates = _.uniq(
        currentTemplates.flatMap((template) =>
          template.topics.map((t) => t.id),
        ),
      ).filter(
        (topicId) =>
          category !== TemplateCategories.MY_TOPICS ||
          myTopicsIdSet.has(topicId),
      );

      return {
        mine: mineTemplates.length,
        unorganized: unorganizedTemplates.length,
        myTopics: myTopicsTemplates.length,
        allTopics: allTopicsTemplates.length,
        current: currentTemplates,
        topicIds: sortTopics(topicIdsWithTemplates, topicMap),
      };
    }, [
      allTemplates,
      category,
      currentUser.id,
      personalData?.session.user.membershipTopics,
      topicMap,
    ]);

  const controls = useMemo(
    () => (
      <>
        <PostFilterMenu<TemplateCategories>
          onChange={setCategory}
          options={{
            [TemplateCategories.MINE]: {
              label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.MINE],
              icon: memberIcon,
              count: mine,
            },
            [TemplateCategories.MY_TOPICS]: {
              label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.MY_TOPICS],
              icon: topicIcon,
              count: myTopics,
            },
            [TemplateCategories.ALL_TOPICS]: {
              label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.ALL_TOPICS],
              icon: topicsIcon,
              count: allTopics,
            },
            [TemplateCategories.UNORGANIZED]: {
              label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.UNORGANIZED],
              icon: unorganizedIcon,
              count: unorganized,
            },
          }}
          value={category}
          title="Show"
        />
        <PostFilterMenu<TopicPostsOrder>
          icon={orderIcon}
          onChange={(order) =>
            dispatch(UIActions.changeTemplatesOrderBy(order))
          }
          options={{
            [TopicPostsOrder.Popularity]: { label: 'Popularity' },
            [TopicPostsOrder.PublishedAt]: { label: 'Recently published' },
            [TopicPostsOrder.UpdatedAt]: { label: 'Recently updated' },
            [TopicPostsOrder.ViewedAt]: { label: 'Recently viewed' },
            [TopicPostsOrder.Title]: { label: 'Alphabetical' },
          }}
          value={sortBy}
          title="Sort"
        />
      </>
    ),
    [mine, myTopics, allTopics, unorganized, category, sortBy, dispatch],
  );

  const renderMeta = (post: AllSlimPostsFragment) => {
    const { views, publishedAt, userViewedAt, updatedAt } = post;

    switch (TOPICS_METADATA_ATTRIBUTE[sortBy]) {
      case 'popularity':
        return `${views} ${inflection.inflect('view', views)}`;
      case 'publishedAt':
        return publishedAt && <Timestamp date={publishedAt} />;
      case 'userViewedAt':
        return userViewedAt && <Timestamp date={userViewedAt} />;
      default:
        return <Timestamp date={updatedAt} />;
    }
  };

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageContent>
        <h1 className={styles.title}>Templates</h1>
      </PageContent>

      {topicIds.length === 0 ? (
        <TopicPostSectionSimpleList
          loading={loading}
          renderMeta={renderMeta}
          title={TEMPLATE_CATEGORIES_LABELS[category]}
          posts={sortTemplates(current, sortBy)}
          refetch={refetch}
          controls={controls}
        />
      ) : (
        topicIds.map((topicId, index) => {
          const topic = topicMap[topicId];
          if (!topic) {
            return null;
          }
          return (
            <TopicPostSectionSimpleList
              key={topicId}
              loading={false}
              aboveTitle={
                <TopicBreadcrumbs
                  link
                  topics={topic.ancestors}
                  size="small"
                  renderName={(name, ancestor) => (
                    <TopicNameWithIcon name={name} topic={ancestor} />
                  )}
                />
              }
              title={<TopicNameWithIcon topic={topic} iconSize="medium" />}
              posts={sortTemplates(
                current.filter((template) =>
                  template.topics.some((t) => t.id === topicId),
                ),
                sortBy,
              )}
              refetch={refetch}
              controls={index === 0 ? controls : null}
              renderMeta={renderMeta}
            />
          );
        })
      )}
      {!loading && (
        <PageContent>
          {!current.length && (
            <div className={styles.empty}>
              <EmptyState imageName="templates" vertical noMargin />
            </div>
          )}
          <TemplatesSuggestions />
        </PageContent>
      )}
    </PageLayout.Content>
  );
};

export default TemplatesView;
